import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { resolveImage, formatINR } from "@/lib/api";
import { PriceBlock } from "@/components/PriceBlock";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, Zap, ChevronRight, Check, Loader2, Truck, ShieldCheck, RotateCcw } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loadRazorpayScript } from "@/lib/razorpay";

const categoryLabel = { Gold: "9K Gold", Silver: "925 Silver", GP: "Gold Plated" };

// Load the Razorpay Checkout script once
export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);
  const [adding, setAdding] = useState(false);
  const [buying, setBuying] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyForm, setBuyForm] = useState({
    name: "", mobile: "", email: "", address: "", city: "", state: "", pincode: "",
  });

  useEffect(() => {
    if (user && user !== false) {
      setBuyForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        mobile: f.mobile || user.mobile || "",
        ...(user.addresses?.[0] || {}),
      }));
    }
  }, [user]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    api.get(`/products/${id}`).then(({ data }) => { setProduct(data); setActiveImg(0); setQty(1); }).catch(() => setNotFound(true));
  }, [id]);

  const guardShop = () => {
    if (!user || user === false) {
      toast.info("Please login to continue");
      navigate("/login");
      return false;
    }
    if (user.role === "admin") {
      toast.info("Switch to a customer account to shop");
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!guardShop()) return;
    setAdding(true);
    try {
      await addToCart(product.id, qty);
    } catch {
      toast.error("Could not add to cart");
    } finally {
      setAdding(false);
    }
  };

  const handleBuyNow = async () => {
    if (!guardShop()) return;
    setBuyForm((f) => ({
      ...f,
      name: f.name || user?.name || "",
      email: f.email || user?.email || "",
      mobile: f.mobile || user?.mobile || "",
    }));
    setBuyOpen(true);
  };

  const setBuyField = (k) => (e) => setBuyForm((f) => ({ ...f, [k]: e.target.value }));

  const startBuyPayment = async (e) => {
    e.preventDefault();
    if (!buyForm.name.trim()) { toast.error("Please enter your name"); return; }
    if (!/^\d{10}$/.test(buyForm.mobile)) { toast.error("Enter a valid 10-digit mobile number"); return; }
    if (!buyForm.address.trim() || !buyForm.city.trim() || !buyForm.state.trim()) { toast.error("Please complete your delivery address"); return; }
    if (!/^\d{6}$/.test(buyForm.pincode)) { toast.error("Enter a valid 6-digit PIN code"); return; }

    setBuying(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok || !window.Razorpay) {
        toast.error("Could not load payment gateway. Please try again.");
        return;
      }
      // 1) Create a Razorpay order on the backend for this product
      const { data: rp } = await api.post("/payments/razorpay/create-order", {
        product_id: product.id,
        quantity: qty,
      });

      // 2) Open the Razorpay checkout popup
      const rzp = new window.Razorpay({
        key: rp.key_id,
        amount: rp.amount,
        currency: rp.currency,
        order_id: rp.order_id,
        name: "ChainSmart",
        description: `${rp.product_name} × ${rp.quantity}`,
        image: "/logo192.png",
        theme: { color: "#C9A227" },
        prefill: {
          name: buyForm.name,
          email: buyForm.email,
          contact: buyForm.mobile,
        },
        handler: async (response) => {
          try {
            const { data: result } = await api.post("/payments/razorpay/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              product_id: product.id,
              quantity: qty,
              ...buyForm,
            });
            setBuyOpen(false);
            toast.success("Payment successful! Your order is confirmed.");
            navigate(`/order-confirmation/${result.order_id}`, { replace: true });
          } catch {
            toast.error("Payment could not be verified. If money was deducted, please contact support.");
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled");
          },
        },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
    } finally {
      setBuying(false);
    }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-3xl mb-4">Product not found</h1>
        <Link to="/catalogue" className="text-gold-dark font-semibold hover:underline">Back to Catalogue</Link>
      </div>
    );
  }

  if (!product) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }

  const images = product.images?.length ? product.images : [null];
  const outOfStock = product.stock <= 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="product-details-page">
      <Breadcrumb className="mb-8">
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator><ChevronRight className="w-3.5 h-3.5" /></BreadcrumbSeparator>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/catalogue">Catalogue</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator><ChevronRight className="w-3.5 h-3.5" /></BreadcrumbSeparator>
          <BreadcrumbItem><BreadcrumbPage>{product.name}</BreadcrumbPage></BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div>
          <div className="relative overflow-hidden bg-white border border-black/10 aspect-square group">
            <img
              src={resolveImage(images[activeImg])}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              data-testid="product-main-image"
            />
            {product.discount_percent > 0 && (
              <span className="absolute top-4 left-4 bg-discount text-white text-xs font-semibold uppercase tracking-wider px-2.5 py-1">
                {product.discount_percent}% OFF
              </span>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 overflow-hidden border ${activeImg === i ? "border-gold" : "border-black/10"}`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={resolveImage(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="overline text-gold-dark">{categoryLabel[product.category] || product.category}</span>
            <span className="text-xs text-muted-foreground">SKU: {product.sku}</span>
          </div>
          <h1 className="font-serif text-4xl lg:text-5xl text-obsidian leading-tight mb-5" data-testid="product-title">{product.name}</h1>

          <PriceBlock mrp={product.mrp} price={product.price} discount={product.discount_percent} size="lg" className="mb-2" />
          {product.discount_percent > 0 && (
            <p className="text-sm text-green-700 font-medium mb-6" data-testid="savings-line">
              You save ₹{(product.mrp - product.price).toLocaleString("en-IN")}
            </p>
          )}

          {product.breakup && (
            <div className="border border-black/10 bg-secondary/50 p-4 mb-6 rounded-sm" data-testid="price-breakup">
              <p className="overline text-gold-dark mb-3">Transparent Price Breakup</p>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">{product.breakup.purity_label} · {product.breakup.weight_grams} g @ ₹{product.breakup.per_gram.toLocaleString("en-IN")}/g</span><span className="font-medium">₹{product.breakup.metal_value.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Making Charge ({product.breakup.making_pct}%)</span><span className="font-medium">₹{product.breakup.making_charge.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">GST ({product.breakup.gst_pct}%)</span><span className="font-medium">₹{product.breakup.gst_amount.toLocaleString("en-IN")}</span></div>
                <div className="flex justify-between border-t border-black/10 pt-1.5"><span className="font-semibold text-obsidian">Total</span><span className="font-bold text-obsidian">₹{product.mrp.toLocaleString("en-IN")}</span></div>
              </div>
            </div>
          )}

          {/* Attributes */}
          <div className="grid grid-cols-2 gap-px bg-black/10 border border-black/10 my-6">
            <Attr label="Gold Purity" value={product.purity} />
            {product.weight && <Attr label="Weight" value={product.weight} />}
            {product.length && <Attr label="Length" value={product.length} />}
            <Attr label="Availability" value={outOfStock ? "Out of Stock" : `${product.stock} in stock`} highlight={outOfStock} />
          </div>

          {/* Quantity */}
          <div className="flex items-center gap-4 mb-6">
            <span className="text-sm font-semibold">Quantity</span>
            <div className="flex items-center border border-black/20 rounded-sm">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5 hover:bg-secondary transition-colors" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(product.stock || 99, q + 1))} className="p-2.5 hover:bg-secondary transition-colors" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <button
              onClick={handleAdd}
              disabled={outOfStock || adding}
              className="flex-1 inline-flex items-center justify-center gap-2 border border-obsidian text-obsidian font-semibold py-3.5 hover:bg-obsidian hover:text-white transition-colors disabled:opacity-40 rounded-sm"
              data-testid="pdp-add-to-cart"
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />} Add to Cart
            </button>
            <button
              onClick={handleBuyNow}
              disabled={outOfStock || buying}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-obsidian font-semibold py-3.5 hover:bg-gold-light transition-colors disabled:opacity-40 rounded-sm"
              data-testid="pdp-buy-now"
            >
              {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} {buying ? "Starting payment…" : "Buy Now"}
            </button>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground mb-8">
            <span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-gold-dark" /> Fast delivery across India</span>
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-gold-dark" /> Secure ordering</span>
            <span className="flex items-center gap-1.5"><RotateCcw className="w-4 h-4 text-gold-dark" /> 7-day returns</span>
          </div>

          {product.description && (
            <div className="border-t border-black/10 pt-6">
              <h3 className="font-serif text-2xl mb-3">Description</h3>
              <p className="text-muted-foreground leading-relaxed" data-testid="product-description">{product.description}</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="buy-now-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Delivery Details</DialogTitle>
          </DialogHeader>
          <form onSubmit={startBuyPayment} className="space-y-4">
            <p className="text-sm text-muted-foreground -mt-1">
              We will ship <span className="font-medium text-obsidian">{product.name}</span> (Qty {qty}) here after payment.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name</Label>
                <Input value={buyForm.name} onChange={setBuyField("name")} className="mt-1.5 rounded-sm" required data-testid="buy-name" />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input value={buyForm.mobile} onChange={setBuyField("mobile")} maxLength={10} className="mt-1.5 rounded-sm" required data-testid="buy-mobile" />
              </div>
              <div className="sm:col-span-2">
                <Label>Email</Label>
                <Input type="email" value={buyForm.email} onChange={setBuyField("email")} className="mt-1.5 rounded-sm" required data-testid="buy-email" />
              </div>
              <div className="sm:col-span-2">
                <Label>Address</Label>
                <Input value={buyForm.address} onChange={setBuyField("address")} className="mt-1.5 rounded-sm" required data-testid="buy-address" />
              </div>
              <div>
                <Label>City</Label>
                <Input value={buyForm.city} onChange={setBuyField("city")} className="mt-1.5 rounded-sm" required data-testid="buy-city" />
              </div>
              <div>
                <Label>State</Label>
                <Input value={buyForm.state} onChange={setBuyField("state")} className="mt-1.5 rounded-sm" required data-testid="buy-state" />
              </div>
              <div>
                <Label>PIN Code</Label>
                <Input value={buyForm.pincode} onChange={setBuyField("pincode")} maxLength={6} className="mt-1.5 rounded-sm" required data-testid="buy-pincode" />
              </div>
            </div>
            <DialogFooter>
              <button
                type="submit"
                disabled={buying}
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-obsidian font-semibold py-3.5 hover:bg-gold-light transition-colors disabled:opacity-50 rounded-sm"
                data-testid="buy-pay-btn"
              >
                {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {buying ? "Starting payment…" : `Pay ${formatINR(product.price * qty)}`}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Attr = ({ label, value, highlight }) => (
  <div className="bg-white p-4">
    <p className="overline text-muted-foreground mb-1">{label}</p>
    <p className={`text-sm font-semibold ${highlight ? "text-discount" : "text-obsidian"} flex items-center gap-1`}>
      {!highlight && label === "Availability" && <Check className="w-3.5 h-3.5 text-green-600" />}
      {value}
    </p>
  </div>
);
